/**
 * Product Card Swatch Handler
 * Handles image swapping and link updating when color swatches are clicked on product cards
 */

/**
 * Initialize swatch handlers for product cards
 * @param {NodeList|Array} cards - Product card elements to initialize
 */
function initializeProductCardSwatches(cards) {
  if (!cards || cards.length === 0) return;

  cards.forEach(card => {
    // Skip if already initialized
    if (card.dataset.swatchesInitialized === 'true') return;

    card.dataset.swatchesInitialized = 'true';
    const productId = card.dataset.productId;

    // Look for variant data - it should be a sibling after the card
    let variantData = card.nextElementSibling;
    if (!variantData || !variantData.hasAttribute('data-product-variants')) {
      // Try finding it globally as fallback
      variantData = document.querySelector(`[data-product-variants="${productId}"]`);
    }

    if (!variantData) return;

    let data;
    try {
      data = JSON.parse(variantData.textContent);
    } catch (e) {
      console.error('Failed to parse variant data:', e);
      return;
    }

    const swatchInputs = card.querySelectorAll('.swatch-input');
    const productImages = card.querySelectorAll('.ratio img, .ratio-sq img');
    const productLink = card.querySelector('.product-card__link');
    const variantIdInput = card.querySelector('input[name="id"]');
    const addToCartButton = card.querySelector('.quick-add[data-add-to-cart]');

    if (productImages.length === 0 || !productLink) return;

    swatchInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        if (!e.target.checked) return;

        const selectedValue = e.target.value;
        const nameMatch = e.target.name.match(/\[([^\]]+)\]$/);

        if (!nameMatch) {
          console.error('Could not extract option name from:', e.target.name);
          return;
        }

        const optionName = nameMatch[1];
        const optionIndex = data.options.indexOf(optionName);

        if (optionIndex === -1) return;

        const variant = data.variants.find(v => v.options[optionIndex] === selectedValue);

        if (variant) {
          // Update image if variant has a featured image
          if (variant.featured_image && variant.featured_image.src) {
            productImages.forEach((img) => {
              img.src = variant.featured_image.src;
              img.srcset = variant.featured_image.srcset;
              img.alt = variant.featured_image.alt || '';
            });
          }

          // Update link to include variant parameter
          const baseUrl = productLink.dataset.productUrl || productLink.href.split('?')[0];
          productLink.href = `${baseUrl}?variant=${variant.id}`;

          // Update hidden variant ID input in the quick add form
          if (variantIdInput) {
            variantIdInput.value = variant.id;
          }

          // Update add to cart button state based on variant availability
          if (addToCartButton) {
            const isAvailable = variant.available;
            addToCartButton.disabled = !isAvailable;

            const textAvailable = addToCartButton.dataset.textAvailable || 'Add to cart';
            const textUnavailable = addToCartButton.dataset.textUnavailable || 'Sold out';
            addToCartButton.textContent = isAvailable ? textAvailable : textUnavailable;
          }
        }
      });
    });
  });
}

// Initialize on DOM load for static content
document.addEventListener('DOMContentLoaded', () => {
  const productCards = document.querySelectorAll('.product-card');
  initializeProductCardSwatches(productCards);
});

// Observe for dynamically added product cards (e.g., from related products section)
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            let cardsToInit = [];

            if (node.classList && node.classList.contains('product-card')) {
              cardsToInit.push(node);
            } else if (node.querySelectorAll) {
              cardsToInit = Array.from(node.querySelectorAll('.product-card'));
            }

            if (cardsToInit.length > 0) {
              initializeProductCardSwatches(cardsToInit);
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// Expose function globally for manual initialization if needed
window.initializeProductCardSwatches = initializeProductCardSwatches;
